import { handleError } from "helpers/sentry";
import { useEffect, useRef, useState } from "react";
import { runPreflight } from "twilio-video";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "store/store";
import { setIsUnsupportedNetwork, setUiErrorScreen } from "store/app.slice";
import { getEndpoint, ProctoringRoute } from "routes/route";
import { ErrorTypes, StorageKeyNames } from "globals/enums";
import { AlertName } from "helpers/alert-type";
import { alertHelper } from "helpers/alert-helper";
import { AlertLogConfig } from "services/session-token";

const usePreflightCheck = () => {
  const streamToken = useAppSelector((state) => state.app.streamToken);
  const streamingProvider = useAppSelector((state) => state.app.configuration.streaming_provider);
  const [preflightCheckRunning, setPreflightCheckRunning] = useState(false);
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const signalingGatewayReachable = useRef(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (streamingProvider === "twilio" && streamToken.length > 0 && !preflightCheckRunning) {
      const preflightTest = runPreflight(streamToken);
      setPreflightCheckRunning(true);
      preflightTest.on("progress", (progress) => {
        console.log("Preflight check progress:", progress);
        // Safari does not support RTCDtlsTransport, so we use 'peerConnectionConnected' to determine if Signaling Gateway is reachable
        if (progress === "dtlsConnected" || progress === "peerConnectionConnected") {
          signalingGatewayReachable.current = true;
        }
      });
      preflightTest.on("completed", (result) => {
        console.log("Preflight check completed:", result);
        if (!signalingGatewayReachable.current) {
          dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.UnsupportedNetwork }));
          navigate(getEndpoint(ProctoringRoute.Error));
        }
      });
      preflightTest.on("failed", (error) => {
        handleError(`Preflight check failed: ${error.message}`);
        console.log("Preflight check failed:", error);
        if (!signalingGatewayReachable.current) {
          dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.UnsupportedNetwork }));
          sessionStorage.setItem(StorageKeyNames.Screen, ProctoringRoute.Error);
          dispatch(setIsUnsupportedNetwork({ isUnsupportedNetwork: true }));
          alertHelper(alertConfig).raiseAlert(
            AlertName.UnsupportedNetworkIdentified,
            dispatch,
            error.message
          );
          navigate(getEndpoint(ProctoringRoute.Error));
        }
      });
    }
  }, [streamToken]);
};

export default usePreflightCheck;
